import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Seo from '@/components/base/Seo';
import TopicCard from '@/components/feature/TopicCard';
import { mockTopics } from '@/mocks/topics';
import { mockCategories } from '@/mocks/categories';

type SortKey = 'heat' | 'latest';

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCat = searchParams.get('cat') ?? 'all';
  const initialQ = searchParams.get('q') ?? '';

  const [activeCat, setActiveCat] = useState(initialCat);
  const [keyword, setKeyword] = useState(initialQ);
  const [sort, setSort] = useState<SortKey>('heat');

  const list = useMemo(() => {
    let result = [...mockTopics];
    if (activeCat !== 'all') result = result.filter((t) => t.category === activeCat);
    const q = keyword.trim();
    if (q) {
      result = result.filter(
        (t) => t.title.includes(q) || t.summary.includes(q) || t.tags.some((tag) => tag.includes(q)),
      );
    }
    result.sort((a, b) =>
      sort === 'heat' ? b.heat - a.heat : new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
    );
    return result;
  }, [activeCat, keyword, sort]);

  const handleCategory = (id: string) => {
    setActiveCat(id);
    const next = new URLSearchParams(searchParams);
    if (id === 'all') next.delete('cat');
    else next.set('cat', id);
    setSearchParams(next, { replace: true });
  };

  const currentCatName = activeCat === 'all' ? '全部话题' : mockCategories.find((c) => c.id === activeCat)?.name ?? '';

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: '热议广场',
      url: 'https://yuanfenq.cn/topics',
      inLanguage: 'zh-CN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://yuanfenq.cn' },
        { '@type': 'ListItem', position: 2, name: '热议广场', item: 'https://yuanfenq.cn/topics' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: list.slice(0, 10).map((t, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://yuanfenq.cn/topic/${t.id}`,
        name: t.title,
      })),
    },
  ];

  return (
    <>
      <Seo
        title="热议广场 | 网报吃瓜网友热议、评论与不同立场汇总"
        description="网报吃瓜热议广场按热度与时间汇总正在被讨论的网络热点，支持按娱乐、社会、影视、体育等分区筛选，查看网友评论、讨论走向与大众态度。"
        keywords="网报吃瓜热议,网报吃瓜评论,网报吃瓜讨论,网络热点,网友热议,舆情风向"
        path="/topics"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">
        <section className="w-full bg-secondary-900 py-10 md:py-14">
          <div className="w-full px-4 md:px-10">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '热议广场' },
              ]}
            />
            <h1 className="mt-4 font-heading text-3xl md:text-5xl font-black text-background-50">热议广场</h1>
            <p className="mt-3 text-sm md:text-base text-background-200/80 max-w-2xl leading-relaxed">
              594 个在观察话题中，正在被最多人讨论的都集中在这里。筛选分区、搜索关键词，就能快速定位你在找的那条讨论。
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-10">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5 pb-6 border-b border-background-200/70">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleCategory('all')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    activeCat === 'all' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  全部
                </button>
                {mockCategories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => handleCategory(cat.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      activeCat === cat.id ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="flex items-center gap-2 h-10 px-3 rounded-md bg-background-100 border border-background-200">
                  <i className="ri-search-line text-foreground-500"></i>
                  <label htmlFor="topics-search" className="sr-only">
                    搜索话题
                  </label>
                  <input
                    id="topics-search"
                    name="q"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="搜索话题关键词"
                    className="w-full sm:w-48 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                  />
                </div>
                <div className="flex items-center h-10 p-1 rounded-full bg-background-100 border border-background-200">
                  <button
                    type="button"
                    onClick={() => setSort('heat')}
                    className={`px-4 h-8 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      sort === 'heat' ? 'bg-foreground-900 text-background-50' : 'text-foreground-600'
                    }`}
                  >
                    按热度
                  </button>
                  <button
                    type="button"
                    onClick={() => setSort('latest')}
                    className={`px-4 h-8 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      sort === 'latest' ? 'bg-foreground-900 text-background-50' : 'text-foreground-600'
                    }`}
                  >
                    按最新
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between text-sm text-foreground-500">
              <span>
                当前分区：<span className="text-foreground-800 font-medium">{currentCatName}</span>
              </span>
              <span>共 {list.length} 个话题</span>
            </div>

            {list.length > 0 ? (
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {list.map((topic, index) => (
                  <TopicCard key={topic.id} topic={topic} rank={sort === 'heat' ? index + 1 : undefined} />
                ))}
              </div>
            ) : (
              <div className="py-20 text-center">
                <span className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-search-eye-line text-2xl"></i>
                </span>
                <p className="mt-4 text-sm text-foreground-600">没有找到匹配的话题，换个关键词或分区试试。</p>
                <button
                  type="button"
                  onClick={() => {
                    setKeyword('');
                    handleCategory('all');
                  }}
                  className="mt-5 px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  重置筛选
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}